import React, { useState, useEffect } from "react"
import { useFirebase } from "../context/FirebaseContext"
import { doc, updateDoc } from "firebase/firestore"
import { db } from "../firebase/firebase"
import { AlertCircle, Trash2 } from "lucide-react"
import UserSettings from "../components/UserSettings"

const SettingsPage = ({
	currentLanguage,
	handleLanguageChange,
	notifications,
	soundEffects,
	handleSettingsChange,
}) => {
	const [notificationsEnabled, setNotificationsEnabled] = useState(
		notifications || false
	)
	const [soundEffectsEnabled, setSoundEffectsEnabled] = useState(
		soundEffects || false
	)
	const [showLanguageSelector, setShowLanguageSelector] = useState(false)
	const [dailyGoal, setDailyGoal] = useState("10")
	const [userData, setUserData] = useState(null)
	const [showResetConfirm, setShowResetConfirm] = useState(false)
	const [isResetting, setIsResetting] = useState(false)

	// Get available languages and current user from Firebase context
	const { availableLanguages, isLoading, currentUser, getUserData } = useFirebase()

	// Fetch user data when user logs in
	useEffect(() => {
		const fetchUserData = async () => {
			if (currentUser) {
				try {
					const data = await getUserData(currentUser.uid)
					setUserData(data)
					
					// Set daily goal from user data if available
					if (data?.learningGoal) {
						const goalMinutes = data.learningGoal.split(" ")[0]
						setDailyGoal(goalMinutes)
					}
				} catch (error) {
					console.error("Error fetching user data:", error)
				}
			}
		}

		fetchUserData()
	}, [currentUser, getUserData])

	// Update local state when props change
	useEffect(() => {
		setNotificationsEnabled(notifications)
		setSoundEffectsEnabled(soundEffects)
	}, [notifications, soundEffects])

	useEffect(() => {
		const savedSettings = localStorage.getItem("hopLanguageAppSettings")
		if (savedSettings) {
			const parsed = JSON.parse(savedSettings)
			setNotificationsEnabled(parsed.notifications ?? false)
			setSoundEffectsEnabled(parsed.soundEffects ?? false)
			
			// Set daily goal from localStorage if no user data
			if (parsed.dailyGoal && !currentUser) {
				setDailyGoal(parsed.dailyGoal)
			}

			// Update parent component if needed
			if (handleSettingsChange) {
				if (parsed.notifications !== notifications) {
					handleSettingsChange("notifications", parsed.notifications ?? false)
				}
				if (parsed.soundEffects !== soundEffects) {
					handleSettingsChange("soundEffects", parsed.soundEffects ?? false)
				}
			}
		}
	}, [currentUser])

	// Toggle notifications
	const toggleNotifications = () => {
		// Request notification permission if enabling
		if (!notificationsEnabled && "Notification" in window) {
			Notification.requestPermission().then(permission => {
				if (permission === "granted") {
					const newValue = true
					setNotificationsEnabled(newValue)
					if (handleSettingsChange) {
						handleSettingsChange("notifications", newValue)
					}

					// Send a test notification
					new Notification("Notifications Enabled", {
						body: "You will now receive notifications from Hop Language App",
					})
				}
			})
		} else {
			const newValue = !notificationsEnabled
			setNotificationsEnabled(newValue)
			if (handleSettingsChange) {
				handleSettingsChange("notifications", newValue)
			}
		}
	}

	// Toggle sound effects
	const toggleSoundEffects = () => {
		const newValue = !soundEffectsEnabled
		setSoundEffectsEnabled(newValue)
		if (handleSettingsChange) {
			handleSettingsChange("soundEffects", newValue)
		}

		if (!soundEffectsEnabled) {
			const audio = new Audio("/sounds/click.mp3")
			audio.volume = 0.5
			audio.play().catch(e => console.log("Audio play failed:", e))
		}
	}

	// Handle daily goal change
	const handleDailyGoalChange = async (newGoal) => {
		setDailyGoal(newGoal)
		
		if (currentUser) {
			// Update in Firebase for logged-in users
			try {
				const userRef = doc(db, "users", currentUser.uid)
				await updateDoc(userRef, {
					learningGoal: `${newGoal} min/day`
				})
			} catch (error) {
				console.error("Error updating daily goal:", error)
			}
		} else {
			// Update in localStorage for non-logged-in users
			const currentSettings = JSON.parse(
				localStorage.getItem("hopLanguageAppSettings") || "{}"
			)
			const updatedSettings = {
				...currentSettings,
				dailyGoal: newGoal,
			}
			localStorage.setItem(
				"hopLanguageAppSettings",
				JSON.stringify(updatedSettings)
			)
		}
	}

	// Handle progress reset
	const handleResetProgress = async () => {
		setIsResetting(true)
		
		try {
			if (currentUser) {
				// Reset Firebase user data
				const userRef = doc(db, "users", currentUser.uid)
				await updateDoc(userRef, {
					streak: 0,
					completedFlashcards: {},
					lastStreakUpdate: null,
					// Keep displayName, email, learningGoal, and settings
				})
			}
			
			// Reset localStorage data (for both logged-in and guest users)
			localStorage.removeItem("hopLanguageAppStreak")
			localStorage.removeItem("hopLanguageAppStreakDate")
			
			// Close confirmation dialog
			setShowResetConfirm(false)
			
			// Show success message (you could add a toast notification here)
			alert("Progress reset successfully! Please refresh the page to see changes.")
			
		} catch (error) {
			console.error("Error resetting progress:", error)
			alert("Error resetting progress. Please try again.")
		} finally {
			setIsResetting(false)
		}
	}
	// Change language
	const onSelectLanguage = langCode => {
		if (handleLanguageChange) {
			handleLanguageChange(langCode)
		}
		setShowLanguageSelector(false)
	}

	// Show loading spinner if data is loading
	if (isLoading) {
		return (
			<div className='flex justify-center items-center h-64'>
				<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3A6E]'></div>
			</div>
		)
	}

	// If there's no current language or available languages, show an error
	if (
		!currentLanguage ||
		!availableLanguages ||
		availableLanguages.length === 0
	) {
		return (
			<div className='py-6'>
				<h2 className='text-xl font-bold mb-6'>Settings</h2>
				<div className='text-[#FF3A6E]'>
					Error loading language data. Please try again later.
				</div>
			</div>
		)
	}

	return (
		<div className='py-6'>
			<h2 className='text-xl font-bold mb-6'>Settings</h2>

			{/* User Settings (Only shown when logged in) */}
			{currentUser && (
				<div className='mb-6'>
					<UserSettings />
				</div>
			)}

			<div className='space-y-4'>
				{/* Notifications Toggle */}
				<div className='bg-[#1E1E1E] rounded-lg p-4'>
					<div className='flex justify-between items-center'>
						<div>
							<span className='text-white'>Notifications</span>
							<p className='text-[#A0A0A0] text-sm'>
								Receive learning reminders
							</p>
						</div>
						<button
							className={`h-6 w-12 rounded-full relative cursor-pointer ${
								notificationsEnabled ? "bg-orange-500" : "bg-[#333333]"
							}`}
							onClick={toggleNotifications}
							aria-label='Toggle notifications'>
							<div
								className={`h-5 w-5 bg-white rounded-full absolute top-0.5 transition-transform ${
									notificationsEnabled
										? "right-0.5 transform translate-x-0"
										: "left-0.5 transform translate-x-0"
								}`}></div>
						</button>
					</div>
				</div>

				{/* Sound Effects Toggle */}
				<div className='bg-[#1E1E1E] rounded-lg p-4'>
					<div className='flex justify-between items-center'>
						<div>
							<span className='text-white'>Sound Effects</span>
							<p className='text-[#A0A0A0] text-sm'>Play sounds for actions</p>
						</div>
						<button
							className={`h-6 w-12 rounded-full relative cursor-pointer ${
								soundEffectsEnabled ? "bg-orange-500" : "bg-[#333333]"
							}`}
							onClick={toggleSoundEffects}
							aria-label='Toggle sound effects'>
							<div
								className={`h-5 w-5 bg-white rounded-full absolute top-0.5 transition-transform ${
									soundEffectsEnabled
										? "right-0.5 transform translate-x-0"
										: "left-0.5 transform translate-x-0"
								}`}></div>
						</button>
					</div>
				</div>

				{/* Current Language */}
				<div className='bg-[#1E1E1E] rounded-lg p-4'>
					<div className='flex justify-between items-center relative'>
						<div>
							<span className='text-white'>Current Language</span>
							<p className='text-[#A0A0A0] text-sm'>Language you're learning</p>
						</div>
						<button
							className='flex items-center gap-2 py-2 px-3 bg-[#2C2C2C] rounded-lg hover:bg-[#3C3C3C] transition-colors'
							onClick={() => setShowLanguageSelector(!showLanguageSelector)}>
							<span className='text-xl mr-1'>{currentLanguage.flag}</span>
							<span className='text-white'>{currentLanguage.name}</span>
						</button>

						{/* Language Selector Dropdown */}
						{showLanguageSelector && (
							<div className='absolute right-0 top-16 bg-[#2C2C2C] rounded-lg p-3 shadow-lg z-10 w-48'>
								{availableLanguages.map(lang => (
									<button
										key={lang.code}
										className={`flex items-center w-full text-left p-2 hover:bg-[#3C3C3C] rounded-lg mb-1 last:mb-0 ${
											currentLanguage.code === lang.code ? "bg-[#3C3C3C]" : ""
										}`}
										onClick={() => onSelectLanguage(lang.code)}>
										<span className='text-xl mr-3'>{lang.flag}</span>
										<div className='flex flex-col'>
											<span className='text-white'>{lang.name}</span>
											<span className='text-[#A0A0A0] text-xs'>
												{lang.nativeName}
											</span>
										</div>
									</button>
								))}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Extra Settings */}
			<h3 className='text-white font-medium mt-8 mb-4'>Application Settings</h3>
			<div className='space-y-4'>
				<div className='bg-[#1E1E1E] rounded-lg p-4'>
					<div className='flex justify-between items-center'>
						<div>
							<span className='text-white'>Reset Progress</span>
							<p className='text-[#A0A0A0] text-sm'>
								Clear all learning progress and streak data
							</p>
						</div>
						<button 
							className='bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center'
							onClick={() => setShowResetConfirm(true)}
						>
							<Trash2 size={16} className='mr-1' />
							Reset
						</button>
					</div>
				</div>
			</div>

			{/* App Information */}
			<div className='mt-8 text-center'>
				<p className='text-[#6E6E6E]'>Hop Language Learning App</p>
				<p className='text-[#6E6E6E] text-sm'>Version 1.0.0</p>
			</div>

			{/* Reset Confirmation Dialog */}
			{showResetConfirm && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
					<div className='bg-[#1E1E1E] rounded-lg p-6 max-w-md w-full'>
						<div className='flex items-center mb-4'>
							<AlertCircle className='text-red-500 mr-3' size={24} />
							<h3 className='text-white font-bold text-lg'>Reset Progress</h3>
						</div>
						
						<p className='text-[#A0A0A0] mb-6'>
							Are you sure you want to reset all your learning progress? This will:
						</p>
						
						<ul className='text-[#A0A0A0] text-sm mb-6 space-y-1'>
							<li className='flex items-start'>
								<span className='text-red-500 mr-2'>•</span>
								<span>Reset your daily streak to 0</span>
							</li>
							<li className='flex items-start'>
								<span className='text-red-500 mr-2'>•</span>
								<span>Clear all completed flashcards</span>
							</li>
							<li className='flex items-start'>
								<span className='text-red-500 mr-2'>•</span>
								<span>Remove all learning progress data</span>
							</li>
						</ul>
						
						<p className='text-red-400 text-sm mb-6 font-medium'>
							This action cannot be undone!
						</p>
						
						<div className='flex space-x-3'>
							<button
								className='flex-1 bg-[#2C2C2C] text-white py-2 px-4 rounded-lg hover:bg-[#3C3C3C] transition-colors'
								onClick={() => setShowResetConfirm(false)}
								disabled={isResetting}
							>
								Cancel
							</button>
							<button
								className='flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center'
								onClick={handleResetProgress}
								disabled={isResetting}
							>
								{isResetting ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
										Resetting...
									</>
								) : (
									<>
										<Trash2 size={16} className='mr-2' />
										Reset Progress
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default SettingsPage